import { useState, useEffect, useRef } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { MdClose } from "react-icons/md";
import {
    InputGroup,
    FieldWrappers,
    Label,
    SubmitBtn,
    SideCancelBtn,
    CardWrapper,
    SelectionCard,
    Wrapper,
    ScrollableContent,
    ActionSection,
    MobileIcon,
    ModalHeader,
} from "../Styles/InputStyles";
import tw from "tailwind-styled-components";

const AddBookingNew = ({ handleClose, addBookingLoading, addBookingMutate }) => {
    const formikRef = useRef(null);
    const [height, setHeight] = useState(window.innerHeight);

    const genderOptions = [
        { value: 1, label: "Male" },
        { value: 2, label: "Female" },
    ];

    const validationSchema = Yup.object().shape({
        name: Yup.string()
            .required("Name is required")
            .matches(
                /^[A-Za-z0-9. ]{4,}$/,
                "Minimum 4 characters, letters, numbers, dot and spaces allowed",
            ),
        mobile: Yup.string()
            .required("Mobile number is required")
            .matches(/^\d{10}$/, "Enter a valid 10-digit mobile number"),
        email: Yup.string()
            .test(
                "email-format",
                "Enter a valid email address",
                (value) => {
                    if (!value || value.trim() === "") return true;
                    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(
                        value.trim(),
                    );
                },
            ),
        gender: Yup.string().required("Please select gender"),
    });

    const initialValues = {
        name: "",
        mobile: "",
        email: "",
        gender: 1,
        remarks: "",
    };

    useEffect(() => {
        const handleResize = () => setHeight(window.innerHeight);
        window.addEventListener("resize", handleResize);
        handleResize();
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const handleSubmit = (values, { resetForm }) => {
        const payload = {
            name: values.name,
            mobile: values.mobile,
            gender: values.gender,
            description: values.remarks
        };
        if (values.email && values.email.trim()) {
            payload.email = values.email.trim();
        }

        addBookingMutate(payload);
    };

    return (
        <Container
            style={{
                height: `${height}px`,
                overflow: "auto",
                WebkitOverflowScrolling: "touch",
            }}
        >
            <ModalHeader>
                <h1 className="text-white text-lg font-medium">
                    Add Booking
                </h1>
                <button onClick={handleClose}>
                    <MdClose color="white" size={24} />
                </button>
            </ModalHeader>

            <div className="px-6 h-full flex flex-col justify-between">
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    innerRef={formikRef}
                    validateOnBlur={true}
                    validateOnChange={true}
                >
                    {({ values, setFieldValue, errors, touched }) => {
                        return (
                            <>
                                <Form>
                                    <ScrollableContent className="max-h-[calc(100dvh-140px)]">
                                        <Wrapper>
                                            {/* Name */}
                                            <InputGroup>
                                                <Label htmlFor="name">
                                                    Name *
                                                </Label>
                                                <FieldWrappers>
                                                    <Field
                                                        type="text"
                                                        name="name"
                                                        id="name"
                                                        placeholder="Enter full name"
                                                        autoComplete="new-password"
                                                    />
                                                </FieldWrappers>
                                                {touched.name &&
                                                    errors.name && (
                                                        <p className="text-rose-600 text-sm">
                                                            {errors.name}
                                                        </p>
                                                    )}
                                            </InputGroup>

                                            {/* Mobile */}
                                            <InputGroup>
                                                <Label htmlFor="mobile">
                                                    Mobile *
                                                </Label>
                                                <FieldWrappers>
                                                    <MobileIcon />
                                                    <Field
                                                        type="text"
                                                        name="mobile"
                                                        id="mobile"
                                                        placeholder="Enter 10-digit mobile number"
                                                        inputMode="numeric"
                                                        maxLength={10}
                                                        autoComplete="new-password"
                                                        className="pl-6"
                                                    />
                                                </FieldWrappers>
                                                {touched.mobile &&
                                                    errors.mobile && (
                                                        <p className="text-rose-600 text-sm">
                                                            {errors.mobile}
                                                        </p>
                                                    )}
                                            </InputGroup>

                                            {/* ✅ Email (optional) */}
                                            <InputGroup>
                                                <Label htmlFor="email">
                                                    Email
                                                </Label>
                                                <FieldWrappers>
                                                    <Field
                                                        type="email"
                                                        name="email"
                                                        id="email"
                                                        placeholder="Enter email address"
                                                        autoComplete="new-password"
                                                    />
                                                </FieldWrappers>
                                                {touched.email &&
                                                    errors.email && (
                                                        <p className="text-rose-600 text-sm">
                                                            {errors.email}
                                                        </p>
                                                    )}
                                            </InputGroup>

                                            {/* Gender */}
                                            <InputGroup>
                                                <Label>Gender *</Label>
                                                <CardWrapper>
                                                    {genderOptions.map(
                                                        (option) => (
                                                            <SelectionCard
                                                                key={
                                                                    option.value
                                                                }
                                                                type="button"
                                                                onClick={(
                                                                    e,
                                                                ) => {
                                                                    e.preventDefault();
                                                                    setFieldValue(
                                                                        "gender",
                                                                        option.value,
                                                                    );
                                                                }}
                                                                className={
                                                                    values.gender ===
                                                                        option.value
                                                                        ? "bg-[#0078BD] text-white"
                                                                        : "bg-gray-100 text-gray-600"
                                                                }
                                                            >
                                                                <span className="text-sm">
                                                                    {
                                                                        option.label
                                                                    }
                                                                </span>
                                                            </SelectionCard>
                                                        ),
                                                    )}
                                                </CardWrapper>
                                                {touched.gender &&
                                                    errors.gender && (
                                                        <p className="text-rose-600 text-sm">
                                                            {errors.gender}
                                                        </p>
                                                    )}
                                            </InputGroup>
                                            <InputGroup>
                                                <Label htmlFor="remarks">Remarks</Label>
                                                <FieldWrapper>
                                                    <Field
                                                        as={Textarea}
                                                        id="remarks"
                                                        name="remarks"
                                                        rows={4}
                                                        maxLength={500}
                                                        placeholder="Write your remark here..."
                                                    />
                                                </FieldWrapper>
                                            </InputGroup>
                                        </Wrapper>
                                    </ScrollableContent>

                                    <ActionSection>
                                        <SideCancelBtn
                                            type="button"
                                            onClick={handleClose}
                                        >
                                            Cancel
                                        </SideCancelBtn>
                                        <SubmitBtn
                                            type="submit"
                                            disabled={addBookingLoading}
                                        >
                                            {addBookingLoading
                                                ? "Submitting..."
                                                : "Submit"}
                                        </SubmitBtn>
                                    </ActionSection>
                                </Form>
                            </>
                        );
                    }}
                </Formik>
            </div>
        </Container>
    );
};

const Container = tw.div`bg-white w-full h-full flex flex-col relative`;
const FieldWrapper = tw.div`w-full`
const Textarea = tw.textarea`min-h-30 border border-gray-300 rounded pl-2 py-2 focus:ring-1 focus:ring-sky-600  focus:border-sky-600 focus:outline-none duration-200 w-full h-30 resize-y  break-words overflow-hidden`;

export default AddBookingNew;